<template>
  <div class="flex flex-col mb-6 py-4 border-b-8 border-grey-lighter relative">
    <div class="flex flex-row items-center justify-between mb-3">
      <div class="flex">
        <!-- Chart Title -->
        <div class="flex flex-row">
          <div class="text-base font-secondary font-semibold">
            {{ title }}{{ !unit || noAvailableData ? `` : ` (${unit})` }}
          </div>

          <div v-if="!loading && noAvailableData">
            <p class="ml-2">({{ noAvailableDataText }})</p>
          </div>
        </div>
      </div>
      <div class="flex">
        <base-icon
          v-if="!loading && !noAvailableData"
          class="text-xl mr-3 text-gray-500 transition hover:text-gray-400 self-center"
          icon="info"
          v-tooltip="{
            content: tooltipInfoText,
            placement: 'left',
          }"
        />

        <!-- Export chart dropdown -->
        <base-dropdown-2
          v-if="!loading && !noAvailableData"
          context="secondary"
          size="sm"
          placement="bottom"
          trigger="click"
        >
          <template><base-icon icon="download" class="text-xl mr-2" /></template>
          <template #body>
            <base-button
              @click="onReportsExport('json')"
              size="sm"
              context="outline"
              class="w-full border-b"
              action="json"
            />
            <base-button
              @click="onReportsExport('csv')"
              size="sm"
              context="outline"
              class="w-full border-b"
              action="csv"
            />
          </template>
        </base-dropdown-2>
        <a v-show="false" ref="exportLink" href="#"></a>
      </div>
    </div>

    <!-- Chart -->
    <base-loader :fetching="loading" />
    <!-- <div v-show="!loading && !noAvailableData" ref="chartElement"></div> -->
    <div
      class="chart-container"
      :class="[noAvailableData ? '' : 'chart-height', 'relative w-full']"
    >
      <canvas v-show="!loading && !noAvailableData" ref="myChart"></canvas>
    </div>
  </div>
</template>

<script>
import {Chart, registerables} from 'chart.js'
import 'chartjs-adapter-date-fns'
//import ApexCharts from 'apexcharts'
import isEmpty from 'lodash-es/isEmpty'
import {BaseButton, BaseButtonIcon, BaseIcon, BaseDropdown2, BaseLoader} from '@/components/base'
import {format} from 'date-fns'
//import chartLocales from '@/i18n/apexChartsLocales.js'

export default {
  components: {
    BaseButton,
    BaseIcon,
    BaseButtonIcon,
    BaseDropdown2,
    BaseLoader,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    unit: {
      type: String,
      default: '',
    },
    payload: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    min: {
      type: String,
    },
    max: {
      type: String,
    },
    showY2: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      default: () => {
        return {}
      },
    },
    includeTime: {
      type: Boolean,
    },
  },

  data() {
    return {
      chart: null,
      graphType: this.config.type || 'line',
      noAvailableDataText: this.$t('mygwf.components.User.ReportChart.no_data'),
    }
  },

  methods: {
    _generateChart(chartOptions) {
      Chart.register(...registerables)
      this.chart = new Chart(this.$refs.myChart, chartOptions)
      this.$nextTick(() => {
        this.chart.render()
      })
    },

    onDataSelected(data) {
      this.$emit('selected', data)
    },

    onReportsExport(type) {
      this.$emit('export', type)
    },
  },

  computed: {
    chartOptions() {
      let context = this
      return {
        type: this.config.type || 'line',
        data: {
          datasets: this.datasets,
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
        },
        options: {
          responsive: true,
          aspectRatio: 4,
          maintainAspectRatio: false,
          onClick: evt => {
            const points = this.chart.getElementsAtEventForMode(
              evt,
              'nearest',
              {intersect: false},
              true
            )

            if (points.length) {
              const firstPoint = points[0]
              const label = this.chart.data.labels[firstPoint.index]
              const value = this.chart.data.datasets[firstPoint.datasetIndex].data[firstPoint.index]

              //console.log(value)
              this.onDataSelected(value)
            }
          },
          elements: {
            point: {
              hitRadius: 2,
              hoverRadius: 6,
              //backgroundColor: '#25679c',
            },
          },
          radius: 3,
          animation: true,
          parsing: true,
          interaction: {
            mode: 'index',
            axis: 'x',
            intersect: false,
          },
          plugins: {
            legend: {
              display: false, //This will do the task
            },
            tooltip: {
              //yAlign: 'bottom',
              titleMarginBottom: 10,
            },
            //decimation: {enabled: true, algorithm: 'min-max'},
          },
          scales: {
            x: {
              min: this.min,
              max: this.max,
              type: 'time',
              minUnit: 'minute',
              time: {
                displayFormats: {
                  quarter: 'MMM YYYY',
                  millisecond: 'HH:mm',
                  second: 'HH:mm',
                  minute: 'HH:mm',
                  hour: 'HH:mm',
                },
                ...this.config.granOptions,
              },
              title: {
                display: true,
              },
              ticks: {
                autoSkip: false,
                maxRotation: 0,
                major: {
                  enabled: true,
                },
                callback: function (value, index, ticks) {
                  if (index === 0 && context.includeTime) {
                    // const gran = ['minute', 'hour'].includes(this.config.granOptions.unit)
                    // console.log(this.config.granOptions)
                    return format(this.min, 'MMM dd')
                  }
                  return value
                },
                // color: function(context) {
                //   return context.tick && context.tick.major ? '#FF0000' : 'rgba(0,0,0,0.1)';
                // },
                font: function (context) {
                  if (context.tick && context.tick.major) {
                    return {
                      weight: 'bold',
                    }
                  }
                },
              },
            },
            y: {
              display: true,
              beginAtZero: true,
              // title: {
              //   display: true,
              //   text: 'value',
              // },
            },
          },
        },
      }
    },
    datasets() {
      return this.payload
    },
    noAvailableData() {
      return isEmpty(this.payload)
    },

    chartLocale() {
      return chartLocales.map(locale => locale.name).includes(this.$i18n.locale)
        ? this.$i18n.locale
        : 'en'
    },

    tooltipInfoText() {
      return this.$t('mygwf.components.User.ReportChart.help_tooltip')
    },
  },

  watch: {
    payload(n, o) {
      if (!this.chart && !isEmpty(n)) {
        // Generating chart when data exists
        //console.log(`generate chart`)
        this._generateChart(this.chartOptions)
      } else if (this.chart && isEmpty(n)) {
        // Destroying chart when data is empty
        this.chart.destroy()
        this.chart = null
      } else if (this.chart && !isEmpty(n)) {
        // Regenerating chart to load new data when data exists
        // console.log(`update chart`)
        //console.log(this.chart.config.data.datasets)
        this.chart.destroy()
        this.chart = null
        this._generateChart(this.chartOptions)

        // this.chart.config.data.datasets[0].data = n
        // this.chart.update()
      }
    },
  },
}
</script>
<style>
.chart-height {
  height: 35vh;
}
.apexcharts-xaxis-label:nth-last-child(2) {
  transform: translateX(-15px);
}
</style>
